import {
  getLangFromPathname,
  localizeUrl,
  stripLocalePrefix,
} from "@/lib/url/localizeUrl"

const DEFAULT_MAX_WAIT_MS = 3000
export const PENDING_HASH_STORAGE_KEY = "mxp_pending_hash"
export const HASH_SCROLL_LOCK_KEY = "mxp_hash_scroll_lock"
export const MXP_DESTINATION_ATTR = "data-mxp-destination"

function normalizeComparablePath(path: string): string {
  let normalized = stripLocalePrefix(path)
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`
  }
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1)
  }
  return normalized
}

export function isSamePageHashPath(
  targetPath: string,
  currentPath: string,
): boolean {
  return (
    normalizeComparablePath(targetPath) === normalizeComparablePath(currentPath)
  )
}

export type HashNavigationTarget = {
  destination: string
  hash: string
  pathname: string
  search: string
}

export function parseHashNavigationTarget(
  href: string,
  lang: string,
  currentPathname = "/",
  currentSearch = "",
): HashNavigationTarget | null {
  if (!href?.includes("#")) return null

  const localized = localizeUrl(href, lang)

  if (localized.startsWith("#")) {
    return {
      destination: `${currentPathname}${currentSearch}${localized}`,
      hash: localized,
      pathname: currentPathname,
      search: currentSearch,
    }
  }

  const hashIndex = localized.indexOf("#")
  if (hashIndex === -1) return null

  const hash = localized.slice(hashIndex)
  const beforeHash = localized.slice(0, hashIndex)
  const url = new URL(beforeHash || "/", "http://local")

  return {
    destination: `${url.pathname}${url.search}${hash}`,
    hash,
    pathname: url.pathname,
    search: url.search,
  }
}

function getAnchorScrollTop(el: HTMLElement): number {
  const navEl = document.querySelector(
    ".site-navigation",
  ) as HTMLElement | null
  const navVisible =
    navEl && !navEl.classList.contains("site-navigation--is-hidden")
  const offset = navVisible ? navEl.offsetHeight + 16 : 80
  return el.getBoundingClientRect().top + window.scrollY - offset
}

export function scheduleScrollToAnchor(hash: string): void {
  sessionStorage.setItem(HASH_SCROLL_LOCK_KEY, hash)

  const attemptScroll = () => scrollToAnchor(hash)

  requestAnimationFrame(() => {
    requestAnimationFrame(attemptScroll)
  })
  // Next.js and RouteTracker can scroll to top after route commit; re-apply.
  window.setTimeout(attemptScroll, 50)
  window.setTimeout(attemptScroll, 150)
  window.setTimeout(attemptScroll, 350)
  window.setTimeout(() => {
    sessionStorage.removeItem(HASH_SCROLL_LOCK_KEY)
  }, 1000)
}

/**
 * Scroll to a URL hash target, retrying until the element exists or the wait
 * budget expires. Accounts for the sticky site navigation offset.
 */
export function scrollToAnchor(
  hash: string,
  options?: { behavior?: ScrollBehavior; maxWaitMs?: number },
): void {
  const rawId = hash.startsWith("#") ? hash.slice(1) : hash
  if (!rawId) return

  const id = decodeURIComponent(rawId)
  const behavior = options?.behavior ?? "smooth"
  const maxWaitMs = options?.maxWaitMs ?? DEFAULT_MAX_WAIT_MS
  const start = Date.now()

  const tryScroll = () => {
    const el = document.getElementById(id)
    if (el) {
      window.scrollTo({ top: getAnchorScrollTop(el), behavior })
      return
    }

    if (Date.now() - start < maxWaitMs) {
      requestAnimationFrame(tryScroll)
    }
  }

  tryScroll()
}

export function storePendingHash(hash: string): void {
  if (hash) {
    sessionStorage.setItem(PENDING_HASH_STORAGE_KEY, hash)
  }
}

export function peekPendingHash(): string {
  return (
    window.location.hash ||
    sessionStorage.getItem(PENDING_HASH_STORAGE_KEY) ||
    ""
  )
}

export function clearPendingHash(): void {
  sessionStorage.removeItem(PENDING_HASH_STORAGE_KEY)
}

export function consumePendingHash(): string {
  const hash = peekPendingHash()
  clearPendingHash()
  return hash
}

export function navigateHashLinkFromClick(
  event: { preventDefault(): void; stopPropagation(): void },
  href: string,
  navigate?: (href: string) => void,
): boolean {
  const lang = getLangFromPathname(window.location.pathname)
  const parsed = parseHashNavigationTarget(
    href,
    lang,
    window.location.pathname,
    window.location.search,
  )
  if (!parsed) return false

  event.preventDefault()

  if (isSamePageHashPath(parsed.pathname, window.location.pathname)) {
    window.history.pushState(null, "", parsed.destination)
    scheduleScrollToAnchor(parsed.hash)
    return true
  }

  storePendingHash(parsed.hash)
  navigate?.(parsed.destination)
  return true
}
